import classNames from 'classnames'
import moment from 'moment'
import { CSSProperties, Fragment, ReactElement, useMemo, useState } from 'react'
import { SectionCard } from 'app/components/SectionCard'
import Spinner from 'app/components/Spinner'
import { hueOutline, hueTint } from 'lib/chartColors'
import { ActivityJson } from 'app/types/entities'

const DAYS = [`Sun`, `Mon`, `Tue`, `Wed`, `Thu`, `Fri`, `Sat`]
const DAYS_FULL = [`Sunday`, `Monday`, `Tuesday`, `Wednesday`, `Thursday`, `Friday`, `Saturday`]
const HOURS = Array.from({ length: 24 }, (_, h) => h)
// Compact per-hour axis label, e.g. 0 -> "12a", 13 -> "1p".
function hourTick(h: number): string {
  const period = h < 12 ? `a` : `p`
  const hr = h % 12 === 0 ? 12 : h % 12
  return `${hr}${period}`
}

function hourLabel(h: number): string {
  if (h === 0) return `12 AM`
  if (h === 12) return `12 PM`
  return h < 12 ? `${h} AM` : `${h - 12} PM`
}

/** Chip recipe per cell: brand blue tint stepping with intensity, hairline ink border. Empty cells stay neutral. */
const CELL_ALPHAS = [0.32, 0.46, 0.6, 0.74]

function cellStyle(count: number, max: number): CSSProperties | undefined {
  if (count <= 0 || max <= 0) return undefined
  const ratio = count / max
  const step = ratio <= 0.25 ? 0 : ratio <= 0.5 ? 1 : ratio <= 0.75 ? 2 : 3
  return { backgroundColor: hueTint(`blue`, CELL_ALPHAS[step]), borderColor: hueOutline(`blue`) }
}

type HoverCell = { day: number; hour: number; count: number; x: number; y: number }

/**
 * Weekday × hour-of-day heatmap of when calls were received, derived from the
 * activities' `activityDate`. Cell intensity is relative to the busiest hour.
 *
 * Note: `activities` is the dashboard's `activitiesState.hits`, fetched with
 * `pagination: { limit: 1000 }`. For very high-volume workspaces over a wide
 * date range the distribution is silently truncated to the 1000 most-recent
 * calls; acceptable for an at-a-glance visual.
 */
export function CallTimeHeatmap({
  activities,
  isLoading,
}: {
  activities: ActivityJson[]
  isLoading?: boolean
}): ReactElement {
  const { counts, max } = useMemo(() => {
    const grid = Array.from({ length: 7 }, () => new Array<number>(24).fill(0))
    for (const activity of activities) {
      if (!activity.activityDate) continue
      // Bucket in UTC so the weekday/hour matches the time shown in the call
      // table (which formats `activityDate` via `formatDate`, i.e. UTC). Using
      // local time here put calls in the wrong cell relative to the table.
      const m = moment.utc(activity.activityDate)
      if (!m.isValid()) continue
      grid[m.day()][m.hour()] += 1
    }
    let maxCount = 0
    for (const row of grid) for (const c of row) if (c > maxCount) maxCount = c
    return { counts: grid, max: maxCount }
  }, [activities])

  const [hover, setHover] = useState<HoverCell | null>(null)

  return (
    <SectionCard attached title="Calls by Time of Day" className="mb-6" contentClassName="bg-surface">
      <div>
        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner size={16} />
          </div>
        ) : (
          <div
            className="grid items-center gap-1"
            style={{ gridTemplateColumns: `2.5rem repeat(24, minmax(0, 1fr))` }}
            onMouseLeave={() => setHover(null)}
          >
            {/* hour axis */}
            <div />
            {HOURS.map((h) => (
              <div key={`axis-${h}`} className="text-center text-body-xs leading-none text-strong">
                {hourTick(h)}
              </div>
            ))}
            {/* day rows */}
            {DAYS.map((day, dayIndex) => (
              <Fragment key={day}>
                <div className="text-body-xs text-subtle">{day}</div>
                {counts[dayIndex].map((count, hour) => (
                  <div
                    key={`${day}-${hour}`}
                    onMouseMove={(event) =>
                      setHover({ day: dayIndex, hour, count, x: event.clientX, y: event.clientY })
                    }
                    className={classNames(`h-7 rounded`, count > 0 ? `border` : `bg-surface-muted`)}
                    style={cellStyle(count, max)}
                  />
                ))}
              </Fragment>
            ))}
          </div>
        )}
      </div>

      {hover && (
        <div
          className="pointer-events-none fixed z-50 rounded-md bg-inverse px-2.5 py-1.5 shadow-lg"
          style={{ top: hover.y + 14, left: hover.x + 14 }}
        >
          <div className="text-ui-xs text-inverse">{`${DAYS_FULL[hover.day]} · ${hourLabel(hover.hour)}`}</div>
          <div className="text-body-xs text-inverse/80">{`${hover.count} call${hover.count === 1 ? `` : `s`}`}</div>
        </div>
      )}
    </SectionCard>
  )
}
